import { useEffect } from 'react';
import { Breadcrumb } from 'react-breadcrumbs';
import { Redirect } from 'react-router-dom';

import CandidateSyncDelayMessage from 'components/Application/Candidates/CandidateSyncDelayMessage';
import LoadingSpinner from '../../library/utils/LoadingSpinner';
import useQueryState from '../../../hooks/use-query-state';
import { ATS } from '../../../types';
import { useApplications } from '../../../hooks/queries/applications';
import { useCandidates } from '../../../hooks/queries/candidates';
import { useSession } from '../../../hooks/use-session';
import { correctPath } from 'libraries/gem';

const atsUsingApplications = [ATS.Gem, ATS.Greenhouse, ATS.SuccessFactors];
const atsUsingCandidates = [ATS.Lever, ATS.Workday];

const CandidateATSRedirect = () => {
  const [id] = useQueryState<string>('id', '');
  const [jobStageExtId] = useQueryState<string>('job_stage_ext_id', '');
  const [jobName] = useQueryState<string>('job_name', '');
  const [src] = useQueryState<string>('src', '');

  const { account } = useSession();

  const {
    data: applications,
    isLoading: isApplicationsLoading,
    refetch: refetchApplications,
  } = useApplications({ ats_id: id, job_stage_ext_id: jobStageExtId }, {
    enabled: Boolean(id && account?.ats_type && atsUsingApplications.includes(account.ats_type)),
  });
  const {
    data: candidates,
    isLoading: isCandidatesLoading,
    refetch: refetchCandidates,
  } = useCandidates({ ats_id: id, job_stage_ext_id: jobStageExtId }, {
    enabled: Boolean(id && account?.ats_type && atsUsingCandidates.includes(account.ats_type)),
  });

  const candidateWasFound = (applications?.applications && applications?.applications?.length > 0) || (candidates?.candidates && candidates?.candidates?.length > 0);

  const isExtensionWithRedesign = src === 'extension' && account?.has_redesign;
  useEffect(() => {
    if (!isExtensionWithRedesign) {
      return;
    }
    if (applications?.applications?.[0]) {
      window.location.href = `/schedule/queue/${applications.applications[0].id}`;
    } else if (candidates?.candidates?.[0]?.applications && candidates.candidates[0].applications.length > 0) {
      const apps = candidates.candidates[0].applications;
      const matchingApplication = jobName
        ? apps.find((app) => app.job?.name === jobName)
        : null;
      const applicationToUse = matchingApplication || apps[0];
      window.location.href = `/schedule/queue/${applicationToUse.id}`;
    }
  }, [isExtensionWithRedesign, applications, candidates, jobName, id]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!candidateWasFound) {
        refetchApplications();
        refetchCandidates();
      }
    }, 1000);
    if (candidateWasFound) {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [candidateWasFound]);

  if (!id) {
    return <Redirect to={correctPath('/app/candidates')} />;
  }

  if (isApplicationsLoading || isCandidatesLoading) {
    return <LoadingSpinner />;
  }

  if (applications?.applications && applications.applications.length > 0) {
    if (!isExtensionWithRedesign) {
      analytics.track('ATS Redirect Succeeded', { id });
      return <Redirect to={correctPath(`/app/candidates/${applications.applications[0].id}`)} />;
    }

    return <LoadingSpinner />;
  }

  if (candidates?.candidates && candidates.candidates.length > 0 && candidates.candidates[0].applications?.length > 0) {
    if (!isExtensionWithRedesign) {
      analytics.track('ATS Redirect Succeeded', { id });

      // Find application that matches the job name, or fall back to first one
      const applications = candidates.candidates[0].applications;
      const matchingApplication = jobName
        ? applications.find((app) => app.job?.name === jobName)
        : null;
      const applicationToUse = matchingApplication || applications[0];
      return <Redirect to={correctPath(`/app/candidates/${applicationToUse.id}`)} />;
    }
    return <LoadingSpinner />;
  }

  analytics.track('ATS Redirect Failed', { id });

  return (
    <Breadcrumb
      data={{
        title: id,
        pathname: correctPath('/app/candidates/ats'),
      }}
    >
      <CandidateSyncDelayMessage />
    </Breadcrumb>
  );
};

export default CandidateATSRedirect;
